<!-- eslint-disable max-len -->
<template>
  <v-card id='mapController'>
    <v-app-bar flat color='rgba(0, 0, 0, 0)'>
      <v-toolbar-title flat class='title'>Scotland's cycling monitoring map</v-toolbar-title>
    </v-app-bar>
    <v-card-subtitle class='sub-text'>
      Below are the locations of Scotland's permanent
      and temporary cycling monitoring infrastructure.
      Click on each point to generate real time data for that site.
      For specific locations or networks of interest filter the sites
      by either geographic area (left filter) and/or network provider (right filter).
    </v-card-subtitle>
    <v-row class='pa-4'>
      <v-col cols='5'>
        <v-select v-model='authority' :items='fetchLocalAuthorities()' label='Area' hint='Select an area'
          persistent-hint outlined></v-select>
      </v-col>
      <v-col cols='5'>
        <v-select v-model='network' :items='fetchNetworks' item-text='label' item-value='provider' label='Provider'
          hint='Select a provider' persistent-hint outlined></v-select>
      </v-col>
      <v-col cols='2' class='text-center'>
        <modal-btn :networkProvider='authority' :localAuthority='network' :download='formatModalOptions'
          :displayToggle='true' :singular='false'></modal-btn>
      </v-col>
    </v-row>
    <v-card-actions>
      <h4 class='legend-title'>Map icons:</h4>
      <div class="legend-element">
        <div class='pa-2 blue darken-4 rounded-circle d-inline-block'></div>
        <span class='pl-2'>Automatic cycle counters</span>
      </div>
      <div class="legend-element">
        <div class='pa-2 red darken-4 rounded-circle d-inline-block'></div>
        <span class='pl-2'>Temporary traffic survey locations</span>
      </div>
      <v-spacer></v-spacer>
      <button class="pr-16" plain @click="disclaimer = !disclaimer">
        Disclaimer<v-icon right v-show="!disclaimer">
          mdi-chevron-right
        </v-icon>
        <v-icon right v-show="disclaimer">
          mdi-chevron-down
        </v-icon>
      </button>
    </v-card-actions>
    <v-expand-transition>
      <div v-show="disclaimer">
        <v-divider></v-divider>
        <v-card-text>
          Disclaimer: Cycling Scotland assumes no responsibility
          or liability for any errors or omissions in the data of this site.
          The information contained in this site is provided on an "as is"
          basis with no guarantees of completeness, accuracy, usefulness or timeliness.
          The cycling counts supplied is raw, uncleaned data. Extended counts of zero,
          or unusually high counts, may be due to a malfunctioning counter.
          Data consumers must decide whether counts are genuine and suitable for use.
        </v-card-text>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
// import iframeModal from '@/components/iframeModal.vue';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import modalBtn from '@/components/Modal/Modal.vue';

export default {
  name: 'MapController',
  components: {
    modalBtn,
  },
  data() {
    return {
      reveal: false,
      id: 'MapControl',
      src: 'map2',
      disclaimer: false,
      years2Pull: moment()
        .subtract(2, 'years')
        .format('YYYY-MM-DD'),
    };
  },
  computed: {
    authority: {
      get() {
        return this.$store.state.map2.selectedAuthority;
      },
      set(val) {
        this.$store.commit('setAuthority', val);
      },
    },
    network: {
      get() {
        return this.$store.state.map2.selectedNetwork;
      },
      set(val) {
        this.$store.commit('setNetwork', val);
      },
    },
    ...mapGetters([
      'fetchNetworks',
    ]),
  },
  watch: {
    authority(value) {
      this.$store.commit('setNetwork', 'All');
      const queries = {
        operators: ['limit(-1)', `ge(startTime,${this.years2Pull})`, 'distinct(location)', 'cache=true'],
      };
      const trafficQueries = { operators: ['limit(-1)', 'distinct(Street)', 'cache=true'] };
      if (value !== 'All') {
        queries.operators.push(`eq(area,${value})`);
        trafficQueries.operators.push(`match(Area,${value})`);
      }
      this.requestAutomaticCyclingCounters(queries);
      this.requestTrafficSurveyData(trafficQueries);
    },
    network() {
      this.$store.commit('toggleLoading', true);
      const queries = {
        operators: ['limit(-1)', `ge(startTime,${this.years2Pull})`, 'distinct(location)', 'cache=true'],
      };
      const trafficQueries = { operators: ['limit(-1)', 'distinct(Street)', 'cache=true'] };
      if (this.authority && this.authority !== 'All') {
        queries.operators.push(`eq(area,${this.authority})`);
        trafficQueries.operators.push(`match(Area,${this.authority})`);
      }
      this.requestAutomaticCyclingCounters(queries);
      this.requestTrafficSurveyData(trafficQueries);
    },
  },
  methods: {
    ...mapGetters([
      'fetchAutomaticCyclingCounters',
      'fetchLocalAuthorities',
      'getTrafficSurveyToggle',
    ]),
    ...mapActions([
      'requestAutomaticCyclingCounters',
      'requestDownloads',
      'requestTrafficSurveyDownload',
      'requestTrafficSurveyData',
    ]),
    async formatModalOptions(startTime, endTime) {
      this.$store.commit('toggleLoading', true);
      let trafficSurveyResponses = null;
      if (this.getTrafficSurveyToggle()) {
        const surveyOperators = [
          'limit(-1)',
          'sort(+StartDateTime)',
          'sort(+Area)',
          `ge(StartDateTime,${startTime})`,
          `le(EndDateTime,${endTime})`,
          'format(csv)',
        ];
        if (this.authority !== 'All') surveyOperators.push(`eq(Area,${this.authority})`);
        const flattenedStartTime = moment(startTime, 'YYYY-MM-DD')
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD');
        const flattenedEndTime = moment(endTime, 'YYYY-MM-DD')
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD');
        trafficSurveyResponses = await this.requestTrafficSurveyDownload({
          operators: surveyOperators,
        });
        Object.assign(trafficSurveyResponses, {
          filename: `trafficSurveys_${this.authority}_${flattenedStartTime}_${flattenedEndTime}`,
        });
      }
      const counterOperators = [
        'limit(-1)',
        'sort(+startTime)',
        'sort(+siteID)',
        `ge(startTime,${startTime})`,
        `le(endTime,${endTime})`,
        'format(csv)',
      ];
      if (this.authority !== 'All') counterOperators.push(`eq(area,${this.authority})`);
      const counterResponses = await this.requestDownloads({
        operators: counterOperators,
        startTime,
        endTime,
      }).catch((e) => {
        if (!this.getTrafficSurveyToggle()) {
          this.$store.commit('toggleLoading', false);
          this.$store.commit('setError', { error: e.message });
        }
      });
      const responses = [counterResponses, trafficSurveyResponses].flat();
      responses.forEach((response) => {
        if (response) {
          const blob = new Blob([response.data], { type: 'application/csv' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${response.filename}.csv`;
          link.click();
          URL.revokeObjectURL(link.href);
        }
      });
      this.$store.commit('toggleLoading', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.legend-title {
  color: #5a5959;
  padding-left: 12px;
}

.legend-element {
  color: #5a5959;
  font-size: 0.875rem;
  padding: 0 10px;
}
</style>
